import { captureException } from '@sentry/react';

import InputInterface from '~/modules/game-engine/input/interface';
import AubioStrategy from '~/modules/game-engine/input/mic-strategies/aubio';
import events from '~/modules/game-events/game-events';
import userMediaService from '~/modules/user-media/user-media-service';
import Listener from '~/modules/utils/listener';

const MAX_RESTART_ATTEMPTS = 5;

class SimplifiedMic extends Listener<[number, number]> implements InputInterface {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;

  private interval: ReturnType<typeof setInterval> | null = null;

  private frequencies: [number, number] = [0, 0];
  private volumes: [number, number] = [0, 0];

  private startedMonitoring = false;
  /** The start in flight, if any - a second caller waits on it rather than opening a second stream. */
  private starting: Promise<boolean> | null = null;
  /** Bumped by every start and stop, so a start that finishes after being superseded lets go of what it opened. */
  private generation = 0;
  /** The restart in progress after the OS took the microphone; cleared by any stop from outside. */
  private restarting: object | null = null;

  public constructor() {
    super();

    // A context created or left running while the page was hidden can come back suspended.
    global?.document?.addEventListener?.('visibilitychange', () => {
      if (!global.document.hidden) this.resumeContext(this.context);
    });
  }

  public startMonitoring = async () => {
    await this.tryStartMonitoring();
  };

  /** Resolves true when the microphone is open and being read. */
  public tryStartMonitoring = async (): Promise<boolean> => {
    if (this.startedMonitoring) return this.starting ?? true;
    this.startedMonitoring = true;

    const generation = ++this.generation;
    this.starting = this.open(generation).finally(() => {
      if (generation === this.generation) this.starting = null;
    });

    return this.starting;
  };

  private open = async (generation: number) => {
    const superseded = () => generation !== this.generation;
    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    const release = () => {
      stream?.getTracks().forEach((track) => track.stop());
      context?.close().catch(() => undefined);
    };

    try {
      stream = await userMediaService.getUserMedia({
        audio: {
          // echoCancellation is turned on because without it there is silence from the mic
          // every other second (possibly some kind of Chrome Mobile bug)
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
        },
        video: false,
      });
    } catch (e) {
      if (!superseded()) this.startedMonitoring = false;
      if (!(e instanceof Error) || e.name !== 'NotAllowedError') {
        captureException(e, { level: 'warning', extra: { message: 'SimplifiedMic.startMonitoring' } });
      }
      console.warn(e);
      return false;
    }

    if (superseded()) {
      release();
      return false;
    }

    try {
      context = new AudioContext({
        sampleRate: 44100,
      });
      this.resumeContext(context);

      const source = context.createMediaStreamSource(stream);

      const analyserCh0 = context.createAnalyser();
      analyserCh0.fftSize = 2048;
      analyserCh0.minDecibels = -100;
      source.connect(analyserCh0);

      const strategy = new AubioStrategy();
      await strategy.init(context, analyserCh0.fftSize);

      if (superseded()) {
        release();
        return false;
      }

      this.stream = stream;
      this.context = context;
      // The OS can take the microphone away (a call, another app); start over rather than read silence.
      stream.getAudioTracks().forEach((track) =>
        track.addEventListener('ended', () => {
          if (!superseded()) void this.restartAfterTrackEnded({}, 1);
        }),
      );

      this.interval = setInterval(async () => {
        const dataCh0 = new Float32Array(analyserCh0.fftSize);

        analyserCh0.getFloatTimeDomainData(dataCh0);
        const freq = await strategy.getFrequency(dataCh0);
        const volume = this.calculateVolume(dataCh0);

        this.frequencies = [freq, freq];

        this.volumes = [volume, volume];

        this.onUpdate(freq, volume);
      }, context.sampleRate / analyserCh0.fftSize);

      events.micMonitoringStarted.dispatch();
      return true;
    } catch (e) {
      release();
      if (!superseded()) this.startedMonitoring = false;
      captureException(e);
      console.error(e);
      return false;
    }
  };

  // Retried with growing gaps: the microphone is often still held by the call that took it.
  private restartAfterTrackEnded = async (token: object, attempt: number) => {
    await this.stopMonitoring();
    if (this.startedMonitoring) return;
    this.restarting = token;

    if (await this.tryStartMonitoring()) return;
    if (this.restarting !== token) return;
    if (attempt >= MAX_RESTART_ATTEMPTS) {
      captureException(new Error('SimplifiedMic could not get the microphone back'), { level: 'warning' });
      return;
    }
    setTimeout(() => {
      if (this.restarting === token && !this.startedMonitoring) void this.restartAfterTrackEnded(token, attempt + 1);
    }, attempt * 2_000);
  };

  private resumeContext = (context: AudioContext | null) => {
    if (context?.state === 'suspended') context.resume().catch((e) => console.warn('AudioContext.resume failed', e));
  };

  public getFrequencies = () => {
    return this.frequencies;
  };
  public getVolumes = () => this.volumes;
  public clearFrequencies = () => undefined;

  public stopMonitoring = async () => {
    this.restarting = null;
    if (!this.startedMonitoring) return;
    this.startedMonitoring = false;
    this.starting = null;
    // A start still waiting on the microphone sees this and closes what it opens.
    this.generation++;

    if (this.interval) clearInterval(this.interval);
    this.interval = null;
    this.stream?.getTracks().forEach(function (track) {
      track.stop();
    });
    this.stream = null;
    const context = this.context;
    this.context = null;
    try {
      await context?.close();
    } catch (e) {
      console.log('MicInput.stoMonitoring error', e);
    }

    events.micMonitoringStopped.dispatch();
  };

  public getInputLag = () => 180;

  private calculateVolume(input: Float32Array) {
    let i;
    let sum = 0.0;
    for (i = 0; i < input.length; ++i) {
      sum += input[i] * input[i];
    }
    return Math.sqrt(sum / input.length);
  }
  public requestReadiness = () => Promise.resolve(true);

  public getStatus = () => 'ok' as const;
}

export default new SimplifiedMic();
